/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filtertool-factory.service.ts
 * Gegenereerd met Junie (AI), 17-11-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Opmerking: Factory-service die meerdere filtertool-instanties kan maken, ophalen en opruimen.
 */

import {Injectable, inject} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import {FiltertoolInstantie} from './filtertool-instantie';
import {ApiService} from '../api/api.service';

/**
 * Service FiltertoolFactoryService
 * Beheert meerdere filter-instanties via id en verzorgt opslag/herstel uit localStorage.
 */
@Injectable({ providedIn: 'root' })
export class FiltertoolFactoryService {
  // Injects (bibliotheektaal aanhouden)
  private readonly api = inject(ApiService);
  private log: Sihwlog;

  // Properties
  private readonly map = new Map<string, FiltertoolInstantie>();

  // Constructor
  /**
   * Abonneert op API-logout events en ruimt alle instanties op bij logout.
   */
  constructor() {
    // initialiseert een concrete logger voor deze service
    this.log = new Sihwlog('debug', 'FiltertoolFactoryService');
    this.api.loggedOut$.subscribe(() => {
      this.leeg();
    });
  }

  // Public methods
  /**
   * Maakt een nieuwe filtertool-instantie of geeft de bestaande met dit id terug.
   * @param id Optioneel id; indien niet opgegeven wordt een random id gegenereerd.
   */
  nieuw(id: string = crypto.randomUUID()): FiltertoolInstantie {
    if (this.map.has(id)) {
      return this.map.get(id)!;
    }
    const instantie = new FiltertoolInstantie(id, this.api);
    this.map.set(id, instantie);
    return instantie;
  }

  /**
   * Haalt een bestaande instantie op via id.
   */
  opId(id: string): FiltertoolInstantie | undefined {
    return this.map.get(id);
  }

  /**
   * Bepaalt de localStorage-sleutel op basis van de context.
   * De standaardcontext is 'planbord' zodat bestaand gebruik onveranderd blijft.
   */
  private sleutel(context: string | undefined): string {
    const ctx = (context && context.trim()) ? context.trim() : 'planbord';
    return `filtertool_boom_${ctx}`;
  }

  /**
   * Leest de huidige filterinstantie uit localStorage voor de gegeven context.
   * Als er niets opgeslagen is of het formaat ongeldig is, wordt een nieuwe lege instantie geretourneerd.
   * De boom wordt via laadBoomVanObject ingesteld wanneer er geldige data aanwezig is.
   */
  leesHuidigeFilter(context?: string): FiltertoolInstantie {
    const instantie = this.nieuw();
    try {
      const json = localStorage.getItem(this.sleutel(context));
      if (json) {
        let obj: any;
        try {
          obj = JSON.parse(json);
        } catch {
          obj = null;
        }
        // Backwards compatible: oud formaat = direct de boomstructuur; nieuw formaat = { boom, geselecteerdFilterId }
        if (obj && typeof obj === 'object') {
          if ('boom' in obj || 'geselecteerdFilterId' in obj) {
            if (obj.boom) {
              try { instantie.laadBoomVanObject(obj.boom); } catch {}
            }
            if ('geselecteerdFilterId' in obj) {
              (instantie as any).geselecteerdFilterId = (obj.geselecteerdFilterId ?? null);
            }
          } else {
            // oud formaat: obj is de boom; negeren als het geen bruikbaar object is
            try { instantie.laadBoomVanObject(obj); } catch {}
          }
        }
      }
    } catch (e) {
      // Logging: gebruik debug wanneer localStorage niet beschikbaar is of bij leesfouten
      try { this.log.debug('Kon filterboom niet uit localStorage lezen', e as any); } catch {}
    }
    return instantie;
  }

  /**
   * Schrijft de filterinstantie met het opgegeven id naar localStorage voor de gegeven context.
   * Er wordt alleen de boom als platte structuur opgeslagen (JSON), geen functies of observables.
   */
  saveHuidigeFilter(id: string, context?: string): void {
    if (!id) {
      return;
    }
    const instantie = this.opId(id);
    if (!instantie) {
      // geen bekende instantie met dit id; niets te doen
      return;
    }
    try {
      const payload = {
        boom: instantie.boomNaarObject(),
        geselecteerdFilterId: (instantie as any).geselecteerdFilterId ?? null
      };
      localStorage.setItem(this.sleutel(context), JSON.stringify(payload));
    } catch (e) {
      try { this.log.debug('Kon filterboom niet naar localStorage schrijven', e as any); } catch {}
    }
  }

  /**
   * Verwijdert een instantie en ruimt eventuele resources op.
   */
  verwijder(id: string): void {
    // NB: huidige FiltertoolInstantie heeft geen expliciete teardown nodig.
    // Voeg hier op termijn opruimacties toe (complete Subjects, etc.) als dat nuttig is.
    this.map.delete(id);
  }

  /**
   * Ruimt alle instanties op (bijv. bij logout).
   */
  leeg(): void {
    this.map.clear();
  }

  // ===== Autorisatie =====
  /**
   * Bepaalt of de filtertool gebruikt mag worden, op basis van minimale leesrechten op relevante tabellen.
   * Alles-of-niets: als één vereiste permissie ontbreekt, dan geen filtertool.
   *
   * Let op: volgens de regel van `api.can(...)` is het eerste argument de `static table` property van het model.
   */
  canFiltertool(): boolean {
    for (const table of ['project','planitem','personeelvast','functie']) {
      if (!this.api.can(table, 'read')) {
        return false;
      }
    }
    return true;
  }
}
